import React from 'react'

import {Rectangle} from '../services/dom-observer'
import {
  PositionPopoverHandler,
  observePositionPopover,
  positionPopover,
} from '../services/position-popover'

import useObserver from './use-observer'
import useTrackRef from './use-track-ref'
import useUpdateEffect from './use-update-effect'

const INITIAL_POPOVER_STYLE: React.CSSProperties = {
  display: 'none',
}

export type UsePopoverPositionProps = {
  /** Positioning is only active when isOpen is true. */
  isOpen: boolean
  /** Customizable positioning handler. */
  positionHandler?: PositionPopoverHandler
}

export type UsePopoverPositionReturnProps<
  TAnchor extends HTMLElement,
  TPopover extends HTMLElement,
> = {
  anchorRef: React.RefCallback<TAnchor | null>
  popoverRef: React.RefCallback<TPopover | null>
  anchorRefObject: React.RefObject<TAnchor | null>
  popoverRefObject: React.RefObject<TPopover | null>
  anchorRect: Rectangle | null
  popoverRect: Rectangle | null
  popoverStyle: React.CSSProperties
}

const usePopoverPosition = <TAnchor extends HTMLElement, TPopover extends HTMLElement>({
  isOpen,
  positionHandler = positionPopover(),
}: UsePopoverPositionProps): UsePopoverPositionReturnProps<TAnchor, TPopover> => {
  const popoverRefObject = React.useRef<TPopover>(null)
  const anchorRefObject = React.useRef<TAnchor>(null)

  const [anchorRect, setAnchorRect] = React.useState<Rectangle | null>(null)
  const [popoverRect, setPopoverRect] = React.useState<Rectangle | null>(null)
  const [popoverStyle, setPopoverStyle] = React.useState<React.CSSProperties>(INITIAL_POPOVER_STYLE)

  const update = () => {
    if (!isOpen || !popoverRefObject.current || !anchorRefObject.current) {
      // Pevent calling set state unnecessarily on initial mount.
      if (popoverStyle !== INITIAL_POPOVER_STYLE) {
        setPopoverStyle(INITIAL_POPOVER_STYLE)
      }
      return () => {}
    }

    return observePositionPopover(
      popoverRefObject.current,
      anchorRefObject.current,
      (anchorRectLocal, popoverRectLocal, popoverState) => {
        setAnchorRect(anchorRectLocal)
        setPopoverRect(popoverRectLocal)

        switch (popoverState) {
          case 'hidden':
            setPopoverStyle({
              display: 'none',
            })
            break
          case 'measure':
            setPopoverStyle({
              position: 'fixed',
              top: 0,
              left: 0,
              width: 'max-content',
              visibility: 'hidden',
            })
            break
          case 'visible':
          default:
            setPopoverStyle({
              position: 'absolute',
              top: popoverRectLocal?.y,
              left: popoverRectLocal?.x,
              width: 'max-content',
            })
            break
        }
      },
      positionHandler
    )
  }

  const {createObserver} = useObserver(update)

  const popoverRef = useTrackRef<TPopover>(element => {
    popoverRefObject.current = element
    return createObserver()
  })

  const anchorRef = useTrackRef<TAnchor>(element => {
    anchorRefObject.current = element
    return createObserver()
  })

  useUpdateEffect(createObserver, [isOpen])

  return {
    anchorRef,
    popoverRef,
    anchorRefObject,
    popoverRefObject,
    anchorRect,
    popoverRect,
    popoverStyle,
  }
}

export default usePopoverPosition
